import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getSubscription } from "@/lib/subscriptionApi";
import { useAuth } from "@/contexts/AuthContext";

const SubscriptionContext = createContext({
  data: null,
  loading: true,
  refresh: async () => {},
});

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      const s = await getSubscription();
      setData(s);
    } catch (e) {
      // Subscription endpoint failing shouldn't crash the app; just log it.
      console.warn("[subscription] could not load:", e?.message || e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ data, loading, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

// Convenience: tells callers whether a given metered resource still has quota left.
// Resource ∈ {"reports","enhancements","downloads"}. crm_access is a boolean.
export const canUse = (sub, resource) => {
  if (!sub) return true; // be permissive when sub not yet loaded; backend enforces anyway
  const remaining = sub.remaining?.[resource];
  if (remaining === "unlimited") return true;
  if (typeof remaining === "number") return remaining > 0;
  return true;
};

export const hasCrmAccess = (sub) => Boolean(sub?.limits?.crm_access);
